import { useEffect, useRef, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { LevelIcon, PriorityIcon, Search, TypeIcon } from "../components/icons"
import { Avatar, Badge, Button, Empty, ErrorNote, Skeleton, StatusPill, relativeTime, useLoad } from "../components/ui"
import { type IssueSearchResult, type IssueSearchSuggestions, type Project, api } from "../lib/api"
import { BoardConfigProvider, DEFAULT_CONFIG } from "../lib/boardConfig"
import { applySuggestion, describeSort, splitHighlights, warningSegments } from "../lib/issueSearch"

// Issue search for one project, as YouTrack's /issues?q=: one box that takes
// the whole query language — `state: Unresolved assignee: me #bug deploy*` —
// with the query in the URL so a search can be pasted to a colleague.
//
// The grammar is the server's. This page sends what was typed and the caret,
// and shows what comes back: suggestions for the token at the caret, results,
// and warnings with offsets to underline.

const PAGE_SIZE = 50

type Item = IssueSearchResult["items"][number]

export function IssuesPage() {
  const { projectId = "" } = useParams()
  const [params, setParams] = useSearchParams()
  const committed = params.get("q") ?? ""
  const project = useLoad(() => api.get<{ project: Project }>(`/projects/${projectId}`), [projectId])

  // Pages accumulate under "Load more"; a new query starts over.
  const [items, setItems] = useState<Item[]>([])
  const [result, setResult] = useState<IssueSearchResult | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    let live = true
    setLoading(true)
    api
      .get<IssueSearchResult>(`/projects/${projectId}/search?q=${encodeURIComponent(committed)}&limit=${PAGE_SIZE}`)
      .then((page) => {
        if (!live) return
        setResult(page)
        setItems(page.items)
        setError(null)
      })
      .catch((err) => live && setError(err))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [projectId, committed])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const page = await api.get<IssueSearchResult>(
        `/projects/${projectId}/search?q=${encodeURIComponent(committed)}&limit=${PAGE_SIZE}&offset=${items.length}`,
      )
      setItems((before) => [...before, ...page.items])
    } catch (err) {
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }

  const search = (q: string) => {
    const next = new URLSearchParams(params)
    if (q.trim()) next.set("q", q.trim())
    else next.delete("q")
    setParams(next)
  }

  const p = project.data?.project
  if (project.error) return <ErrorNote error={project.error} />
  if (!project.loading && !p) return <Empty title="Project not found" />

  return (
    <BoardConfigProvider value={p?.config ?? DEFAULT_CONFIG}>
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-4">
          <div className="flex items-center gap-2 text-xs text-faint">
            <Link to="/projects" className="hover:text-muted hover:underline">
              Projects
            </Link>
            <span>/</span>
            <Link to={`/projects/${projectId}`} className="hover:text-muted hover:underline">
              {p?.name ?? "Board"}
            </Link>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Search</h1>
          <QueryBox
            key={committed}
            projectId={projectId}
            initial={committed}
            warnings={result?.warnings ?? []}
            onSearch={search}
          />
          {result && result.warnings.length > 0 && (
            <ul className="flex flex-col gap-0.5 text-xs text-warning">
              {result.warnings.map((w) => (
                <li key={`${w.start}-${w.message}`}>{w.message}</li>
              ))}
            </ul>
          )}
        </header>

        <ErrorNote error={error} />

        {loading && !items.length ? (
          <Skeleton className="h-64" />
        ) : items.length ? (
          <section className="flex flex-col gap-2">
            <p className="text-xs text-faint">
              <span className="font-mono tabular-nums text-muted">{result?.total ?? items.length}</span>{" "}
              {result?.total === 1 ? "item" : "items"}
              {result?.sort.length ? <> · sorted by {describeSort(result.sort)}</> : null}
            </p>
            <ul className="flex flex-col">
              {items.map((item) => (
                <ResultRow key={item.id} item={item} />
              ))}
            </ul>
            {result && items.length < result.total && (
              <div className="flex justify-center pt-2">
                <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : `Load more (${result.total - items.length} left)`}
                </Button>
              </div>
            )}
          </section>
        ) : committed ? (
          <Empty title="Nothing matches this query" hint="Check the underlined parts, or remove a filter." />
        ) : (
          <Empty title="No items in this project yet" />
        )}

        <SyntaxHelp />
      </div>
    </BoardConfigProvider>
  )
}

function QueryBox({
  projectId,
  initial,
  warnings,
  onSearch,
}: {
  projectId: string
  initial: string
  warnings: IssueSearchResult["warnings"]
  onSearch: (q: string) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const underlay = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(initial)
  const [suggestions, setSuggestions] = useState<IssueSearchSuggestions | null>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const timer = useRef<number | undefined>(undefined)
  // Responses can overtake each other; only the latest request may land.
  const sequence = useRef(0)

  const suggest = (q: string, caret: number) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(async () => {
      const mine = ++sequence.current
      try {
        const next = await api.get<IssueSearchSuggestions>(
          `/projects/${projectId}/search/suggest?q=${encodeURIComponent(q)}&caret=${caret}`,
        )
        if (mine !== sequence.current) return
        setSuggestions(next)
        setActive(0)
      } catch {
        // A failed suggestion is not worth an error on screen; typing works.
      }
    }, 120)
  }
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const pick = (option: IssueSearchSuggestions["options"][number]) => {
    if (!suggestions) return
    const next = applySuggestion(value, suggestions.from, suggestions.to, option.insert)
    setValue(next.value)
    requestAnimationFrame(() => {
      input.current?.setSelectionRange(next.caret, next.caret)
      input.current?.focus()
    })
    suggest(next.value, next.caret)
  }

  const options = suggestions?.options ?? []
  const showing = open && options.length > 0
  // Underlines belong to the query they were computed for; once it is edited
  // their offsets no longer line up, so they go until the next search.
  const segments = value === initial ? warningSegments(value, warnings) : []

  return (
    <form
      role="search"
      className="relative flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        setOpen(false)
        onSearch(value)
      }}
    >
      <div className="relative min-w-0 flex-1 rounded-md bg-surface">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" />
        {/* The query again, transparent, under the input: the only way to
            underline a stretch of an <input>'s text. Same font, padding and
            scroll position, so the lines sit under the right characters. */}
        <div
          ref={underlay}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-md border border-transparent py-2 pr-3 pl-9 font-mono text-sm whitespace-pre text-transparent"
        >
          {segments.map((s, i) =>
            s.mark ? (
              <span key={i} className="underline decoration-warning decoration-wavy underline-offset-4">
                {s.text}
              </span>
            ) : (
              <span key={i}>{s.text}</span>
            ),
          )}
        </div>
        <input
          ref={input}
          value={value}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          aria-label="Search query"
          aria-autocomplete="list"
          aria-expanded={showing}
          aria-controls="issue-search-suggestions"
          placeholder="state: Unresolved assignee: me #bug deploy*"
          onChange={(event) => {
            setValue(event.target.value)
            setOpen(true)
            suggest(event.target.value, event.target.selectionStart ?? event.target.value.length)
          }}
          onFocus={(event) => {
            setOpen(true)
            suggest(event.target.value, event.target.selectionStart ?? event.target.value.length)
          }}
          onClick={(event) => {
            const target = event.currentTarget
            suggest(target.value, target.selectionStart ?? target.value.length)
          }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onScroll={(event) => {
            if (underlay.current) underlay.current.scrollLeft = event.currentTarget.scrollLeft
          }}
          onKeyDown={(event) => {
            if (!showing) return
            if (event.key === "ArrowDown") {
              event.preventDefault()
              setActive((i) => (i + 1) % options.length)
            } else if (event.key === "ArrowUp") {
              event.preventDefault()
              setActive((i) => (i - 1 + options.length) % options.length)
            } else if (event.key === "Tab" || (event.key === "Enter" && !event.metaKey && !event.ctrlKey)) {
              // Enter picks while the list is open, as in YouTrack; a second
              // Enter, with the list closed, runs the search.
              event.preventDefault()
              pick(options[active]!)
            } else if (event.key === "Escape") {
              setOpen(false)
            }
          }}
          className="relative w-full rounded-md border border-border bg-transparent py-2 pr-3 pl-9 font-mono text-sm text-foreground outline-none placeholder:text-muted focus:border-interactive"
        />
        {showing && (
          <ul
            id="issue-search-suggestions"
            role="listbox"
            className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-lg"
          >
            {options.map((option, i) => (
              <li
                key={`${option.insert}-${i}`}
                role="option"
                aria-selected={i === active}
                // mousedown, not click: click lands after the input's blur has
                // closed the list.
                onMouseDown={(event) => {
                  event.preventDefault()
                  pick(option)
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center justify-between gap-4 px-3 py-1.5 text-sm ${
                  i === active ? "bg-surface-muted text-foreground" : "text-muted"
                }`}
              >
                <span className="truncate font-mono">{option.label}</span>
                <span className="shrink-0 text-xs text-faint">{option.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button type="submit" size="md">
        Search
      </Button>
    </form>
  )
}

function ResultRow({ item }: { item: Item }) {
  const fromComment = item.commentId !== null
  return (
    <li className="flex flex-col gap-1 border-b border-border py-2.5 pr-1 pl-1 last:border-0 hover:bg-surface-muted/60">
      <div className="flex items-center gap-3">
        <LevelIcon level={item.level} />
        {item.level !== "epic" && <TypeIcon type={item.type} />}
        <PriorityIcon priority={item.priority} />
        <Link to={`/tasks/${item.id}`} className="font-mono text-[11px] text-faint hover:text-muted">
          {item.key}
        </Link>
        <Link to={`/tasks/${item.id}`} className="min-w-0 flex-1 truncate text-sm text-foreground hover:underline">
          {item.title}
        </Link>
        <div className="hidden shrink-0 items-center gap-1 md:flex">
          {item.archivedAt && <Badge tone="warning">Archived</Badge>}
          {item.sprintName && (
            <Badge tone="accent" title="Sprint">
              {item.sprintName}
            </Badge>
          )}
          {item.labels.slice(0, 2).map((label) => (
            <Badge key={label}>{label}</Badge>
          ))}
        </div>
        <StatusPill status={item.status} />
        <span className="hidden w-16 shrink-0 text-right text-[10px] text-faint lg:inline" title="Last change">
          {relativeTime(item.updatedAt)}
        </span>
        <Avatar name={item.assigneeName} id={item.assigneeId} size="xs" />
      </div>
      {item.snippet && (
        <p className="ml-7 line-clamp-2 text-xs text-muted">
          {fromComment && <span className="mr-1.5 text-faint">In a comment:</span>}
          {splitHighlights(item.snippet).map((s, i) =>
            s.mark ? (
              <mark key={i} className="rounded-sm bg-warning-soft px-0.5 text-foreground">
                {s.text}
              </mark>
            ) : (
              <span key={i}>{s.text}</span>
            ),
          )}
        </p>
      )}
    </li>
  )
}

const EXAMPLES: [string, string][] = [
  ["deploy failure", "Both words, anywhere: key, title, description or a comment"],
  ['"null pointer"', "The exact phrase"],
  ["deploy*", "Words starting with deploy (there is no stemming)"],
  ["-flaky", "Without the word"],
  ["MXX-12", "That item first, then items that mention it"],
  ["state: Unresolved", "Any status not in a done column; also Resolved or a status name"],
  ["state: {In review}, Blocked", "Braces for spaces, commas for either"],
  ["assignee: me  -assignee: me  assignee: Unassigned", "Who it is assigned to"],
  ["#bug  #{needs review}", "Any field's value: a type, status, priority or label"],
  ["level: epic  parent: MXX-1  epic: MXX-1", "Where it sits"],
  ["sprint: {Current sprint}", "Also {No sprint} or a sprint name"],
  ["created: {This week}  due: 2026-01-01 .. 2026-01-31", "Dates: Today, Yesterday, {Last month}, ranges"],
  ["has: comments  has: -assignee", "Has or lacks: comments, assignee, due, sprint, parent, estimate…"],
  ["summary: checkout  comments: timeout", "Words in one place only"],
  ["archived: yes", "Archived items (any for both)"],
  ["sort by: priority, updated asc", "Relevance first when there is text, otherwise latest change"],
]

function SyntaxHelp() {
  return (
    <details className="rounded-md border border-border px-3 py-2 text-xs text-muted">
      <summary className="cursor-pointer text-foreground">Query syntax</summary>
      <table className="mt-2 w-full">
        <tbody>
          {EXAMPLES.map(([query, meaning]) => (
            <tr key={query} className="align-top">
              <td className="py-1 pr-4 font-mono whitespace-pre text-foreground">{query}</td>
              <td className="py-1">{meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}
